import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Target, Loader2, AlertCircle } from 'lucide-react';
import { BulletCard } from './DetailCard';

const CompetitorTab = () => {
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [currentTools, setCurrentTools] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const analyze = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('competitor-analysis', {
        body: { industry, companySize, currentTools },
      });

      if (fnErr) throw fnErr;
      setResult(typeof data === 'string' ? JSON.parse(data) : data);
    } catch (err: any) {
      setError(err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="w-5 h-5 text-primary" />
            Competitor Analysis
          </CardTitle>
          <CardDescription>Generate competitive positioning for a prospect.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="ca-industry">Industry</Label>
              <Input id="ca-industry" placeholder="e.g. Financial Services" value={industry} onChange={(e) => setIndustry(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ca-size">Company Size</Label>
              <Input id="ca-size" placeholder="e.g. mid-market (200-1000)" value={companySize} onChange={(e) => setCompanySize(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="ca-tools">Current Tools</Label>
              <Input id="ca-tools" placeholder="e.g. Zapier, basic RPA" value={currentTools} onChange={(e) => setCurrentTools(e.target.value)} />
            </div>
          </div>
          <Button onClick={analyze} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Generate Analysis
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-border border-red-500">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-500">{error}</span>
          </CardContent>
        </Card>
      )}

      {result && (
        <>
          {result.positioning && (
            <Card className="border-border">
              <CardHeader><CardTitle className="text-base">Positioning Statement</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-muted-foreground">{result.positioning}</p>
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            {result.vsEnterprise?.length > 0 && <BulletCard title="vs Enterprise Integrators" items={result.vsEnterprise} colorClass="text-primary" />}
            {result.vsBoutique?.length > 0 && <BulletCard title="vs Boutique Agencies" items={result.vsBoutique} colorClass="text-amber-600" />}
            {result.vsNiche?.length > 0 && <BulletCard title="vs Niche Automation" items={result.vsNiche} colorClass="text-green-600" />}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {result.recommendedApproach && (
              <Card className="border-border">
                <CardHeader><CardTitle className="text-base">Recommended Approach</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-muted-foreground">{result.recommendedApproach}</p></CardContent>
              </Card>
            )}
            {result.estimatedROI && (
              <Card className="border-border">
                <CardHeader><CardTitle className="text-base">Estimated ROI</CardTitle></CardHeader>
                <CardContent><p className="text-sm font-semibold text-primary">{result.estimatedROI}</p></CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CompetitorTab;
